import { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { formatDistanceToNow, isValid } from "date-fns";
import { enUS } from "date-fns/locale";
import { es } from "date-fns/locale";
import { seenMessageService } from "../../../infrastructure/services/NotificationsService";
import { chatUserPath } from "../../routes/routes";
import {
  LangContext,
  UserDataContext,
} from "../../contexts/Contexts";
import {
  getChatEmojiCatalog,
  readCachedChatEmojis,
} from "../../../infrastructure/utils/chatEmojiCache";
import { parseReactionMessage } from "../../../infrastructure/utils/chatReactionPreview";
import { getReactionTargetText } from "../../../infrastructure/utils/chatReactionPreviewText";
import { hasUnreadMessages } from "../utils/messageUnread";
import "../../styles/buttons.css";
import "../../styles/notifications.css";

const Message = ({ message, onNavigate }) => {
  const { lang } = useContext(LangContext);
  const { userData } = useContext(UserDataContext);
  const [emojiCatalog, setEmojiCatalog] = useState(() => readCachedChatEmojis());
  const isSpanish = lang?.toLowerCase?.().startsWith("es");
  const userPhoto = message?.fotoPerfilUrl ?? message?.avatar ?? "";

  useEffect(() => {
    if (emojiCatalog.length) {
      return;
    }

    let isMounted = true;

    getChatEmojiCatalog()
      .then((emojis) => {
        if (isMounted) {
          setEmojiCatalog(emojis || []);
        }
      })
      .catch((error) => {
        console.error("Error loading preview emojis:", error);
      });

    return () => {
      isMounted = false;
    };
  }, [emojiCatalog.length]);

  const truncateMessage = (msg = "") => {
    if (msg.length > 58) {
      return `${msg.slice(0, 58)}...`;
    }

    return msg;
  };

  const fullName = useMemo(() => {
    if (message?.apellidos) {
      return `${message?.nombres ?? message?.nombre ?? ""} ${message.apellidos}`.trim();
    }

    return message?.nombre || message?.name || message?.nombres || (isSpanish ? "Usuario VivaNomada" : "VivaNomada User");
  }, [isSpanish, message?.apellidos, message?.name, message?.nombre, message?.nombres]);

  const relativeTime = useMemo(() => {
    const rawDate = message?.fechaEnvio ?? message?.timestamp;
    const date = new Date(rawDate);

    if (!rawDate || !isValid(date)) return null;

    return formatDistanceToNow(date, {
      addSuffix: true,
      locale: isSpanish ? es : enUS,
    });
  }, [isSpanish, message]);

  const messagePreview = useMemo(() => {
    const parsedReaction = parseReactionMessage(message?.mensaje, emojiCatalog);

    if (!parsedReaction) {
      return truncateMessage(message?.mensaje);
    }

    const emojiIcon = parsedReaction.emoji?.Icon?.trim?.() || "";
    const emojiLabel =
      parsedReaction.emoji?.Name ||
      parsedReaction.emoji?.Native ||
      parsedReaction.reactionCode;
    const targetMessage = truncateMessage(
      getReactionTargetText(parsedReaction.afterCode) ||
        (isSpanish ? "tu mensaje" : "your message")
    );
    const reactionText = isSpanish
      ? { before: "Reacciono ", after: ` a ${targetMessage}` }
      : { before: "Reacted ", after: ` to ${targetMessage}` };

    return (
      <>
        {reactionText.before}
        {emojiIcon ? (
          <img
            src={emojiIcon}
            alt={emojiLabel}
            style={{ width: "16px", height: "16px", verticalAlign: "text-bottom" }}
          />
        ) : (
          parsedReaction.emoji?.Native || parsedReaction.emoji?.Name || "\u{1F60A}"
        )}
        {reactionText.after}
      </>
    );
  }, [emojiCatalog, isSpanish, message?.mensaje]);

  const targetMessageId =
    message?.idMensaje ??
    message?.idMessage ??
    message?.messageId ??
    message?.idUltimoMensaje ??
    null;
  const chatState = {
    idUsuario:
      message?.idUsuarioOrigen ?? message?.idUsuario ?? message?.id,
    idChat: message?.idChat ?? message?.IdChat ?? null,
    profPic: userPhoto,
    fotoPerfilUrl: userPhoto,
    nombre: fullName,
    nombres: message?.nombres ?? fullName.split(" ")[0] ?? "",
    apellidos:
      message?.apellidos ?? fullName.split(" ").slice(1).join(" ") ?? "",
    idNacionalidad: message?.idNacionalidad,
    nickName: message?.nickName,
    estatus: message?.estatus,
    puedeVer: message?.puedeVer,
    puedeMandarMensaje: message?.puedeMandarMensaje,
    privacidadMensaje: message?.privacidadMensaje ?? null,
    privacidadTexto: message?.privacidadTexto ?? null,
    mensaje: message?.mensaje ?? "",
    idMensaje: targetMessageId,
    lastMessageId: targetMessageId,
    scrollTarget: {
      id: `message-menu-${targetMessageId ?? Date.now()}`,
      type: "latest-message",
      messageId: targetMessageId,
      messageText: message?.mensaje ?? "",
      timestamp: message?.fechaEnvio ?? message?.timestamp ?? null,
    },
  };

  const handleMessageClick = () => {
    onNavigate?.();

    const contactId =
      message?.idUsuarioOrigen ??
      message?.idAmigo ??
      message?.idPersona ??
      message?.idUsuario ??
      null;

    if (!contactId || !userData?.idUsuario) {
      return;
    }

    window.dispatchEvent(
      new CustomEvent("messages:mark-contact-read", {
        detail: { contactId: String(contactId) },
      }),
    );

    seenMessageService({
      IdUsuario: userData.idUsuario,
      IdAmigo: contactId,
    })
      .then(() => {
        window.dispatchEvent(new CustomEvent("messages:refresh-notifications"));
      })
      .catch((error) => {
        console.error("Failed marking message notification as read", error);
      });
  };

  return (
    <Link
      to={chatUserPath}
      state={chatState}
      className="message-menu-card"
      onClick={handleMessageClick}
    >
      <div className="message-menu-card__avatar-wrap">
        <img
          src={userPhoto || "/img/icons/UserFalse.svg"}
          alt={fullName}
          className="message-menu-card__avatar"
        />
      </div>

      <div className="message-menu-card__body">
        <p className="message-menu-card__text">
          <strong>{fullName}</strong>
        </p>
        <p className="message-menu-card__preview">
          {messagePreview}
        </p>
        {relativeTime ? (
          <span className="message-menu-card__time">{relativeTime}</span>
        ) : null}
      </div>

      {hasUnreadMessages(message) ? (
        <span className="message-menu-card__dot" />
      ) : null}
    </Link>
  );
};

export default Message;
